import { ElementStatus, imageNeedsAltText, resizeImageUrl, useSuggestionsData } from '@wix/rules-panel-ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BiCtaNames, BiModalTypes } from '../../../../../../../bi/types';
import { PanoramaErrorNames } from '../../../../../../../panorama/constants';
import { showGenerateAllErrorModal } from '../../../../../../modals/show-generate-all-error-modal';
export const useAiGeneration = _ref => {
  let {
    images,
    setImages,
    setModifiedImageIds,
    setHasChanges,
    setHasAiGeneratedContent,
    setShowSummary,
    itemType,
    pageId,
    currentPage,
    pageSize,
    a11yWizardAdapter,
    editorHost,
    biLogger,
    reportErrorPanorama,
    t,
    onGenerateComplete
  } = _ref;
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingImageIds, setGeneratingImageIds] = useState(new Set());

  // Track AI-generated images that haven't been reviewed yet
  // (i.e., user hasn't navigated to the page where they are displayed)
  const unreviewedAiImageIds = useRef(new Set());

  // Get the IDs of images on the current page
  const currentPageImageIds = useRef(new Set());
  currentPageImageIds.current = new Set(images.slice(currentPage * pageSize, (currentPage + 1) * pageSize).map(img => img.id));

  // When the page changes, remove current page images from unreviewed set
  useEffect(() => {
    const currentIds = currentPageImageIds.current;
    unreviewedAiImageIds.current = new Set([...unreviewedAiImageIds.current].filter(id => !currentIds.has(id)));
  }, [currentPage]);

  // Mark an image as reviewed (called when user interacts with it)
  const markImageAsReviewed = useCallback(imageId => {
    unreviewedAiImageIds.current.delete(imageId);
  }, []);

  // AI suggestions hook
  const {
    getMultipleSuggestionsData,
    getSingleSuggestionsData
  } = useSuggestionsData({
    failedToGenerateMessage: t('accessibility-wizard.alt-text.failedToGenerate', {
      defaultValue: 'Failed to generate alt text'
    })
  });

  // Generate AI for multiple images
  const handleGenerateAiForImages = useCallback(async imagesToGenerate => {
    if (imagesToGenerate.length === 0) {
      return;
    }
    setIsGenerating(true);
    const generatingIds = new Set(imagesToGenerate.map(img => img.id));
    setGeneratingImageIds(generatingIds);
    try {
      const imageRequests = await Promise.all(imagesToGenerate.map(async img => ({
        entityId: img.entityId ?? pageId,
        id: img.id,
        url: await resizeImageUrl(img.url)
      })));
      const results = await getMultipleSuggestionsData(itemType, imageRequests);
      const successfullyGeneratedIds = new Set();
      setImages(prev => prev.map(img => {
        var _result$suggestions;
        if (!generatingIds.has(img.id)) {
          return img;
        }
        const result = results.find(r => r.id === img.id);
        if (result != null && (_result$suggestions = result.suggestions) != null && _result$suggestions[0]) {
          successfullyGeneratedIds.add(img.id);
          return {
            ...img,
            altText: result.suggestions[0],
            status: ElementStatus.GeneratedAll
          };
        }
        return {
          ...img,
          status: ElementStatus.Warning
        };
      }));
      setModifiedImageIds(prev => {
        const next = new Set(prev);
        successfullyGeneratedIds.forEach(id => next.add(id));
        return next;
      });

      // Track AI-generated images that are not on the current page as unreviewed
      successfullyGeneratedIds.forEach(id => {
        if (!currentPageImageIds.current.has(id)) {
          unreviewedAiImageIds.current.add(id);
        }
      });
      const atLeastOneSucceeded = successfullyGeneratedIds.size > 0;
      if (!atLeastOneSucceeded) {
        reportErrorPanorama(new Error('Failed to generate alt text suggestions'), PanoramaErrorNames.altTextOnGenerateMultipleWithAi);
        showGenerateAllErrorModal({
          a11yWizardAdapter,
          biLogger,
          editorHost,
          handleCancel: () => {
            biLogger.modalCtaClick({
              ctaName: BiCtaNames.Cancel,
              modalType: BiModalTypes.GenerateWithAiError
            });
          },
          handleTryAgain: () => {
            handleGenerateAiForImages(imagesToGenerate);
            biLogger.modalCtaClick({
              ctaName: BiCtaNames.TryAgain,
              modalType: BiModalTypes.GenerateWithAiError
            });
          },
          t
        });
        return;
      }
      setHasChanges(true);
      setHasAiGeneratedContent(true);
      setShowSummary(true);
      onGenerateComplete == null || onGenerateComplete();
    } catch (error) {
      console.error('Failed to generate alt text for multiple images', error);
      setImages(prev => prev.map(img => generatingIds.has(img.id) ? {
        ...img,
        status: ElementStatus.Warning
      } : img));
      showGenerateAllErrorModal({
        a11yWizardAdapter,
        biLogger,
        editorHost,
        handleCancel: () => {},
        handleTryAgain: () => handleGenerateAiForImages(imagesToGenerate),
        t
      });
    } finally {
      setIsGenerating(false);
      setGeneratingImageIds(new Set());
    }
  }, [a11yWizardAdapter, biLogger, editorHost, getMultipleSuggestionsData, itemType, onGenerateComplete, pageId, reportErrorPanorama, setHasAiGeneratedContent, setHasChanges, setImages, setModifiedImageIds, setShowSummary, t]);
  const handleGenerateAi = useCallback(async () => {
    const imagesToGenerate = images.filter(img => imageNeedsAltText(img) && img.isAiSupported !== false);
    await handleGenerateAiForImages(imagesToGenerate);
  }, [images, handleGenerateAiForImages]);

  // Retry failed images
  const handleRetryFailed = useCallback(async failedImages => {
    await handleGenerateAiForImages(failedImages);
  }, [handleGenerateAiForImages]);

  // Sort unsupported images
  const handleSortUnsupported = useCallback(sortedImages => {
    setImages(sortedImages);
  }, [setImages]);

  // Generate AI for single image
  const handleGenerateSingleImage = useCallback(async imageId => {
    const targetImage = images.find(img => img.id === imageId);
    if (!targetImage || targetImage.isAiSupported === false) {
      return;
    }
    setGeneratingImageIds(prev => new Set(prev).add(imageId));
    try {
      var _result$suggestions2;
      const resizedUrl = await resizeImageUrl(targetImage.url);
      const result = await getSingleSuggestionsData(itemType, {
        entityId: targetImage.entityId ?? pageId,
        id: imageId,
        url: resizedUrl
      });
      if ((_result$suggestions2 = result.suggestions) != null && _result$suggestions2[0]) {
        setImages(prev => prev.map(img => img.id === imageId ? {
          ...img,
          altText: result.suggestions[0],
          status: ElementStatus.GeneratedAll
        } : img));
        setModifiedImageIds(prev => new Set(prev).add(imageId));
        setHasChanges(true);
        setHasAiGeneratedContent(true);
        onGenerateComplete == null || onGenerateComplete();
      } else {
        setImages(prev => prev.map(img => img.id === imageId ? {
          ...img,
          status: ElementStatus.Warning
        } : img));
      }
    } catch (error) {
      console.error('Failed to generate alt text for single image', error);
      setImages(prev => prev.map(img => img.id === imageId ? {
        ...img,
        status: ElementStatus.Warning
      } : img));
    } finally {
      setGeneratingImageIds(prev => {
        const next = new Set(prev);
        next.delete(imageId);
        return next;
      });
    }
  }, [getSingleSuggestionsData, images, itemType, onGenerateComplete, pageId, setHasAiGeneratedContent, setHasChanges, setImages, setModifiedImageIds]);
  return {
    generatingImageIds,
    handleGenerateAi,
    handleGenerateAiForImages,
    handleGenerateSingleImage,
    handleRetryFailed,
    handleSortUnsupported,
    isGenerating,
    markImageAsReviewed,
    setGeneratingImageIds,
    setIsGenerating,
    unreviewedAiImageIds
  };
};
//# sourceMappingURL=use-ai-generation.js.map